import React, { useEffect, useMemo, useState } from 'react';
import axios from '../services/axios';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import {
  OPCIONES_POLITICA_RESTRICCION,
  POLITICA_SIN_RESTRICCION,
  etiquetaPoliticaRestriccion,
  descripcionPoliticaRestriccion,
} from '../utils/restriccionesPago';

function RestriccionesPagoAdmin() {
  const { API_URL } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [restricciones, setRestricciones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsuarioId, setSelectedUsuarioId] = useState('');
  const [politica, setPolitica] = useState(OPCIONES_POLITICA_RESTRICCION[1].value);
  const [motivo, setMotivo] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientesFiltrados = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtrados = clientes.filter((cliente) => {
      if (!term) return true;
      return (
        cliente.nombre?.toLowerCase().includes(term) ||
        cliente.padron?.toLowerCase().includes(term) ||
        cliente.direccion?.toLowerCase().includes(term)
      );
    });

    return filtrados.slice(0, 80);
  }, [clientes, searchTerm]);

  const clienteSeleccionado = useMemo(
    () => clientes.find((cliente) => String(cliente.id) === String(selectedUsuarioId)),
    [clientes, selectedUsuarioId],
  );

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [clientesRes, restriccionesRes] = await Promise.all([
        axios.get(`${API_URL}/usuarios`),
        axios.get(`${API_URL}/restricciones-pago`),
      ]);

      const soloClientes = clientesRes.data
        .filter((usuario) => usuario.rol === 'cliente')
        .sort((a, b) => (a.padron || '').localeCompare(b.padron || '', 'es-AR', { numeric: true }));

      setClientes(soloClientes);
      setRestricciones(restriccionesRes.data);
      if (!selectedUsuarioId && soloClientes.length > 0) {
        setSelectedUsuarioId(String(soloClientes[0].id));
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'No se pudieron cargar las restricciones de pago.',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (clearMessage = true) => {
    setEditingId(null);
    setPolitica(OPCIONES_POLITICA_RESTRICCION[1].value);
    setMotivo('');
    if (clearMessage) {
      setMessage(null);
    }
  };

  const handleEdit = (restriccion) => {
    setEditingId(restriccion.id);
    setSelectedUsuarioId(String(restriccion.usuario.id));
    setPolitica(restriccion.politica);
    setMotivo(restriccion.motivo || '');
    setSearchTerm(restriccion.usuario.padron || restriccion.usuario.nombre || '');
    setMessage(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedUsuarioId) {
      setMessage({ type: 'error', text: 'Seleccioná un cliente.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const restriccionActiva = restricciones.find(
        (restriccion) => String(restriccion.usuario?.id) === String(selectedUsuarioId),
      );

      let successText = 'Restricción guardada correctamente.';

      if (politica === POLITICA_SIN_RESTRICCION) {
        const idADesactivar = editingId || restriccionActiva?.id;
        if (idADesactivar) {
          await axios.delete(`${API_URL}/restricciones-pago/${idADesactivar}`);
        }
        successText = 'El cliente quedó sin restricción de pasarela.';
      } else if (editingId) {
        await axios.put(`${API_URL}/restricciones-pago/${editingId}`, { politica, motivo });
        successText = 'Restricción actualizada correctamente.';
      } else {
        await axios.post(`${API_URL}/restricciones-pago`, {
          usuarioId: Number(selectedUsuarioId),
          politica,
          motivo,
        });
      }

      resetForm(false);
      await cargarDatos();
      setMessage({ type: 'success', text: successText });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'No se pudo guardar la restricción.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDesactivar = async (restriccion) => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.delete(`${API_URL}/restricciones-pago/${restriccion.id}`);
      setMessage({ type: 'success', text: 'Restricción desactivada correctamente.' });
      await cargarDatos();
      if (editingId === restriccion.id) {
        resetForm(false);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'No se pudo desactivar la restricción.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen bg-background-light dark:bg-background-dark">
      <Sidebar />

      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-[#111618] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]">
                Restricciones de Pago
              </h1>
              <p className="mt-2 text-sm text-[#617c89] dark:text-gray-400">
                Definí qué pasarela puede usar cada cliente desde el portal.
              </p>
            </div>
            <button
              onClick={resetForm}
              className="flex h-10 items-center justify-center gap-2 rounded-lg border border-primary/40 px-4 text-sm font-bold text-primary transition-colors hover:bg-primary/10"
            >
              <span className="material-symbols-outlined text-xl">restart_alt</span>
              Limpiar
            </button>
          </header>

          {message && (
            <div
              className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
                message.type === 'error'
                  ? 'border-red-200 bg-red-50 text-red-800'
                  : 'border-green-200 bg-green-50 text-green-800'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
            <form
              onSubmit={handleSubmit}
              className="rounded-xl border border-[#dbe2e6] bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-background-dark"
            >
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined">rule_settings</span>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#111618] dark:text-white">
                    {editingId ? 'Editar restricción' : 'Nueva restricción'}
                  </h2>
                  <p className="text-xs text-[#617c89] dark:text-gray-400">
                    Una sola regla activa por cliente.
                  </p>
                </div>
              </div>

              <label className="mb-4 block">
                <span className="mb-2 block text-sm font-semibold text-[#111618] dark:text-white">
                  Buscar cliente
                </span>
                <div className="flex h-12 items-center rounded-lg bg-[#f0f3f4] px-3 dark:bg-gray-700">
                  <span className="material-symbols-outlined text-[#617c89] dark:text-gray-400">search</span>
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Padrón, nombre o dirección"
                    className="h-full min-w-0 flex-1 bg-transparent px-2 text-sm text-[#111618] outline-none placeholder:text-[#617c89] dark:text-white"
                  />
                </div>
              </label>

              <label className="mb-4 block">
                <span className="mb-2 block text-sm font-semibold text-[#111618] dark:text-white">
                  Cliente
                </span>
                <select
                  value={selectedUsuarioId}
                  onChange={(event) => setSelectedUsuarioId(event.target.value)}
                  className="h-12 w-full rounded-lg border-none bg-[#f0f3f4] px-4 text-sm text-[#111618] outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                >
                  {clientesFiltrados.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.padron || 'Sin padrón'} · {cliente.nombre}
                    </option>
                  ))}
                </select>
              </label>

              {clienteSeleccionado && (
                <div className="mb-4 rounded-lg border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
                  <p className="font-semibold">{clienteSeleccionado.nombre}</p>
                  <p className="mt-1 text-xs">
                    Padrón {clienteSeleccionado.padron || '-'} · {clienteSeleccionado.direccion || 'Sin dirección'}
                  </p>
                </div>
              )}

              <label className="mb-4 block">
                <span className="mb-2 block text-sm font-semibold text-[#111618] dark:text-white">
                  Política
                </span>
                <select
                  value={politica}
                  onChange={(event) => setPolitica(event.target.value)}
                  className="h-12 w-full rounded-lg border-none bg-[#f0f3f4] px-4 text-sm text-[#111618] outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                >
                  {OPCIONES_POLITICA_RESTRICCION.map((opcion) => (
                    <option key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-[#617c89] dark:text-gray-400">
                  {descripcionPoliticaRestriccion(politica)}
                </p>
              </label>

              <label className="mb-5 block">
                <span className="mb-2 block text-sm font-semibold text-[#111618] dark:text-white">
                  Motivo interno
                </span>
                <textarea
                  value={motivo}
                  onChange={(event) => setMotivo(event.target.value)}
                  rows={4}
                  placeholder="Ej: debe abonar por Mercado Pago por pagos parciales reiterados."
                  className="w-full resize-none rounded-lg border-none bg-[#f0f3f4] px-4 py-3 text-sm text-[#111618] outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </label>

              <button
                type="submit"
                disabled={saving || loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-bold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">refresh</span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">save</span>
                    Guardar política
                  </>
                )}
              </button>
            </form>

            <section className="overflow-hidden rounded-xl border border-[#dbe2e6] bg-white shadow-sm dark:border-gray-700 dark:bg-background-dark">
              <div className="flex items-center justify-between border-b border-[#dbe2e6] px-5 py-4 dark:border-gray-700">
                <div>
                  <h2 className="text-lg font-bold text-[#111618] dark:text-white">Restricciones activas</h2>
                  <p className="text-xs text-[#617c89] dark:text-gray-400">
                    {restricciones.length} cliente{restricciones.length === 1 ? '' : 's'} con pasarela limitada
                  </p>
                </div>
                <button
                  onClick={cargarDatos}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#617c89] transition-colors hover:bg-primary/10 hover:text-primary"
                  title="Actualizar"
                >
                  <span className="material-symbols-outlined">refresh</span>
                </button>
              </div>

              {loading ? (
                <div className="p-8 text-center text-[#617c89]">Cargando restricciones...</div>
              ) : restricciones.length === 0 ? (
                <div className="p-8 text-center">
                  <span className="material-symbols-outlined mb-3 text-5xl text-gray-300">verified_user</span>
                  <p className="text-sm font-semibold text-[#111618] dark:text-white">Sin restricciones activas</p>
                  <p className="mt-1 text-xs text-[#617c89] dark:text-gray-400">
                    Todos los clientes pueden usar las pasarelas disponibles.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr className="border-b border-[#dbe2e6] dark:border-gray-700">
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[#617c89]">Cliente</th>
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[#617c89]">Política</th>
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[#617c89]">Motivo</th>
                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-[#617c89]">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#dbe2e6] dark:divide-gray-700">
                      {restricciones.map((restriccion) => (
                        <tr key={restriccion.id}>
                          <td className="px-5 py-4">
                            <p className="text-sm font-semibold text-[#111618] dark:text-white">
                              {restriccion.usuario?.nombre || '-'}
                            </p>
                            <p className="mt-1 text-xs text-[#617c89] dark:text-gray-400">
                              {restriccion.usuario?.padron || 'Sin padrón'}
                              {restriccion.usuario?.zona?.nombre ? ` · Zona ${restriccion.usuario.zona.nombre}` : ''}
                            </p>
                          </td>
                          <td className="px-5 py-4">
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                              {etiquetaPoliticaRestriccion(restriccion.politica)}
                            </span>
                          </td>
                          <td className="max-w-sm px-5 py-4 text-sm text-[#617c89] dark:text-gray-400">
                            {restriccion.motivo || '-'}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button
                              onClick={() => handleEdit(restriccion)}
                              className="mr-2 rounded-full p-2 text-primary transition-colors hover:bg-primary/10"
                              title="Editar"
                            >
                              <span className="material-symbols-outlined text-xl">edit</span>
                            </button>
                            <button
                              onClick={() => handleDesactivar(restriccion)}
                              className="rounded-full p-2 text-red-600 transition-colors hover:bg-red-50"
                              title="Desactivar"
                              disabled={saving}
                            >
                              <span className="material-symbols-outlined text-xl">block</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default RestriccionesPagoAdmin;
